import { useMemo, createContext, useContext } from 'react'
import { useLocalStorage } from '../../utilities/hooks/useLocalStorage'
import posthog from 'posthog-js'
import { suppressAutoLogin } from 'modules/miniapp'
import { releaseWebPushOnLogout } from 'modules/webPush'

type TAuthProviderProps = {
    children: React.ReactNode
}

export const AuthContext = createContext<IAuthContext>({
    user: null,
    login: () => null,
    logout: () => null,
    update: () => null,
})
export const AuthProvider = ({ children }: TAuthProviderProps) => {
    const [user, setUser] = useLocalStorage('user', null)

    const login = async (data: IUser) => {
        const user = { ...data }
        setUser(user)
    }

    const update = async (data: IUser) => {
        setUser(data)
    }

    const logout = () => {
        // First: it reads the session it is letting go of from localStorage.
        releaseWebPushOnLogout()
        setUser(null)
        posthog.reset()
        window.localStorage.clear()
        // Inside a mini app the launch payload outlives this call and would
        // silently sign the user back in on the next render. No-op on the web.
        suppressAutoLogin()
    }

    const value = useMemo(
        () => ({
            user,
            login,
            logout,
            update,
        }),
        [user]
    )
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
    return useContext(AuthContext)
}
